import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';

export interface PanelSectionProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}

/**
 * Lightest-weight grouping inside a Panel body — a small caps label plus
 * content, no chrome.  Use to break a long form into thematic sections
 * without introducing another bordered panel.
 */
export function PanelSection({ title, actions, className, children, ...rest }: PanelSectionProps) {
  return (
    <div
      className={cn(
        'phx-panel-section gap-phx-3 [&_+_.phx-panel-section]:mt-phx-6 flex flex-col',
        className
      )}
      {...rest}
    >
      {(title || actions) && (
        <div className="phx-panel-section-head gap-phx-4 flex items-center justify-between">
          {title ? (
            <div className="phx-panel-section-title text-phx-xs text-phx-text-2 font-semibold tracking-wider uppercase">
              {title}
            </div>
          ) : (
            <span />
          )}
          {actions ? (
            <div className="phx-panel-section-actions gap-phx-2 flex">{actions}</div>
          ) : null}
        </div>
      )}
      <div className="phx-panel-section-body gap-phx-3 flex flex-col">{children}</div>
    </div>
  );
}
